/**
 * celeb-room-api-serverless wrappers. Every call requires HMAC + Bearer.
 *
 * All requests go through the BFF proxy at /proxy/celeb-room, where HMAC signing
 * happens server-side (BACKEND_SECRET_KEY never leaves the server) — same model
 * as the DM feature. We reuse the generic `dmRequest` client for JSON calls and
 * add a dedicated multipart helper for photo uploads.
 *
 * Endpoints (version 16.1.x):
 *   Room:     POST/GET/PATCH/DELETE /api/v0/celeb/room[...]
 *   Messages: /api/v0/celeb/room/{room_id}/messages[...]
 *   React:    /api/v0/celeb/room/{room_id}/messages/{message_id}/reactions
 *   Enter:    POST /api/v0/celeb/room/{room_id}/enter
 *   Fans:     GET  /api/v0/celeb/room/{room_id}/fans
 *   Feed:     GET  /api/v0/celeb/rooms  and  /api/v0/celeb/room/by-celeb/{celeb_user_id}
 *   Push:     PATCH /api/v0/celeb/room/{room_id}/push-preference
 */

import { dmRequest, ApiError } from './dmApiClient';
import { getClientId } from './clientId';

// BFF proxy base path (same origin — no secrets in the browser)
export const CELEB_ROOM_PROXY = '/proxy/celeb-room';
const APP_PLATFORM = process.env.REACT_APP_APP_PLATFORM || 'web-admin';

const API_PREFIX = '/api/v0/celeb';

function unwrap(res) {
  return res?.data ?? null;
}

// ── Room management (Partner Celeb) ─────────────────────────────────────────

export async function getMyCelebRoom(accessToken) {
  const res = await dmRequest(CELEB_ROOM_PROXY, `${API_PREFIX}/room`, {
    method: 'GET',
    accessToken,
  });
  return unwrap(res);
}

export async function createCelebRoom(accessToken) {
  const res = await dmRequest(CELEB_ROOM_PROXY, `${API_PREFIX}/room`, {
    method: 'POST',
    body: {},
    accessToken,
  });
  return unwrap(res);
}

export async function disableCelebRoom(roomId, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/disable`,
    { method: 'PATCH', body: {}, accessToken }
  );
  return unwrap(res);
}

export async function deleteCelebRoom(roomId, accessToken) {
  const res = await dmRequest(CELEB_ROOM_PROXY, `${API_PREFIX}/room/${roomId}`, {
    method: 'DELETE',
    accessToken,
  });
  return unwrap(res);
}

export async function setFanListVisibility(roomId, visible, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/fan-list-visibility`,
    { method: 'PATCH', body: { is_fan_list_visible: Boolean(visible) }, accessToken }
  );
  return unwrap(res);
}

// ── Global feed ─────────────────────────────────────────────────────────────

export async function getCelebRoomsFeed(
  { page = 0, size = 20, query = '', sort = 'fans' } = {},
  accessToken
) {
  const res = await dmRequest(CELEB_ROOM_PROXY, `${API_PREFIX}/rooms`, {
    method: 'GET',
    query: { page, size, query: query || undefined, sort: sort || undefined },
    accessToken,
  });
  const d = unwrap(res) || {};
  return {
    items: Array.isArray(d.items) ? d.items : [],
    page: Number(d.page ?? page),
    size: Number(d.size ?? size),
    total: Number(d.total ?? 0),
  };
}

export async function getCelebRoomByCelebId(celebUserId, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/by-celeb/${celebUserId}`,
    { method: 'GET', accessToken }
  );
  return unwrap(res);
}

// ── Messages ────────────────────────────────────────────────────────────────

export async function getCelebRoomMessages(roomId, { page = 0, size = 20 } = {}, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/messages`,
    { method: 'GET', query: { page, size }, accessToken }
  );
  const d = unwrap(res) || {};
  return {
    items: Array.isArray(d.items) ? d.items : [],
    page: Number(d.page ?? page),
    size: Number(d.size ?? size),
    total: Number(d.total ?? 0),
    deletedMessageIds: Array.isArray(d.deleted_message_ids) ? d.deleted_message_ids : [],
  };
}

export async function postCelebRoomMessage(roomId, content, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/messages`,
    { method: 'POST', body: { content }, accessToken }
  );
  return unwrap(res);
}

export async function deleteCelebRoomMessage(roomId, messageId, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/messages/${messageId}`,
    { method: 'DELETE', accessToken }
  );
  return unwrap(res);
}

/**
 * Upload a photo message (multipart/form-data). Sent through the BFF proxy so
 * HMAC signing happens server-side; we only attach x-client-id + Bearer here.
 * Do NOT set Content-Type manually — the browser adds the multipart boundary.
 *
 * @param {number|string} roomId
 * @param {File} file  image file (jpg/jpeg/png, <= 10MB)
 * @param {string} accessToken
 */
export async function postCelebRoomMessagePhoto(roomId, file, accessToken) {
  const form = new FormData();
  form.append('photo', file);

  const url = `${CELEB_ROOM_PROXY}${API_PREFIX}/room/${roomId}/messages/photo`;
  const headers = {
    Accept: 'application/json',
    'x-client-id': getClientId(),
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (APP_PLATFORM) headers['x-app-platform'] = APP_PLATFORM;

  let res;
  try {
    res = await fetch(url, { method: 'POST', headers, body: form });
  } catch (err) {
    throw new ApiError(err?.message || 'Network error', { status: 0 });
  }

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const code =
      (data && typeof data === 'object' && typeof data.code === 'number' ? data.code : 0) || 0;
    const msg =
      (data && typeof data === 'object' && (data.msg || data.message)) || `HTTP ${res.status}`;
    throw new ApiError(msg, { status: res.status, code, data });
  }
  return data?.data ?? null;
}

// ── Emoji reactions (Fan) ───────────────────────────────────────────────────

export async function postReaction(roomId, messageId, emojiId, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/messages/${messageId}/reactions`,
    { method: 'POST', body: { emoji_id: emojiId }, accessToken }
  );
  return unwrap(res);
}

export async function deleteReaction(roomId, messageId, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/messages/${messageId}/reactions`,
    { method: 'DELETE', accessToken }
  );
  return unwrap(res);
}

export async function getReactions(roomId, messageId, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/messages/${messageId}/reactions`,
    { method: 'GET', accessToken }
  );
  const d = unwrap(res) || {};
  return Array.isArray(d.items) ? d.items : Array.isArray(d.reactions) ? d.reactions : [];
}

// ── Enter / fans ────────────────────────────────────────────────────────────

export async function enterCelebRoom(roomId, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/enter`,
    { method: 'POST', body: {}, accessToken }
  );
  return unwrap(res);
}

export async function getCelebRoomFans(roomId, { page = 0, size = 20 } = {}, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/fans`,
    { method: 'GET', query: { page, size }, accessToken }
  );
  const d = unwrap(res) || {};
  return {
    roomId: d.room_id ?? roomId,
    items: Array.isArray(d.items) ? d.items : [],
    page: Number(d.page ?? page),
    size: Number(d.size ?? size),
    total: Number(d.total ?? 0),
  };
}

// ── Push preference (Fan) ───────────────────────────────────────────────────

export async function patchPushPreference(roomId, enabled, accessToken) {
  const res = await dmRequest(
    CELEB_ROOM_PROXY,
    `${API_PREFIX}/room/${roomId}/push-preference`,
    { method: 'PATCH', body: { is_push_enabled: Boolean(enabled) }, accessToken }
  );
  return unwrap(res);
}
